import * as ast from 'notebook/tables/excess/AST';

export interface FunctionCostConfig {
  baseCost: number;
  perArgCost: number;
}

export enum CostBreakdownKey {
  CELL_COUNT = 'Cell Count',
}
export interface CostBreakdown {
  [key: string | CostBreakdownKey]: number;
}

export interface FunctionCostRegistry {
  [functionName: string]: FunctionCostConfig;
}

export const DEFAULT_FUNCTION_COST_REGISTRY: FunctionCostRegistry = {
  DEFAULT: {baseCost: 5, perArgCost: 2},
};

const PARSE_ERROR_COST = 1;

export function computeNodeCost(
  node: ast.ASTNode,
  costConfig: FunctionCostRegistry,
  costBreakdown: {[key: string]: number}
): number {
  if (node instanceof ast.Value) {
    return 1;
  }
  if (node instanceof ast.ReferenceErrorNode) {
    return 1;
  }
  if (node instanceof ast.Cell || node instanceof ast.RelCell) {
    return 1;
  }
  if (node instanceof ast.CellRange) {
    const rows = Math.abs(node.end.row - node.start.row) + 1;
    const cols = Math.abs(node.end.column - node.start.column) + 1;
    return 1 + rows * cols;
  }
  if (node instanceof ast.RelCellRange) {
    return 1;
  }
  if (node instanceof ast.UnaryOperator) {
    return 1 + computeNodeCost(node.child, costConfig, costBreakdown);
  }
  if (node instanceof ast.BinaryOperator) {
    const leftCost = node.left ? computeNodeCost(node.left, costConfig, costBreakdown) : 0;
    const rightCost = node.right ? computeNodeCost(node.right, costConfig, costBreakdown) : 0;
    const name = node.type.toString();
    costBreakdown[name] = (costBreakdown[name] ?? 0) + 1;
    return 1 + leftCost + rightCost;
  }
  if (node instanceof ast.FunctionOperator) {
    const config = costConfig[node.type] ?? costConfig.DEFAULT ?? DEFAULT_FUNCTION_COST_REGISTRY.DEFAULT;
    const selfCost = config.baseCost + config.perArgCost * node.children.length;
    const name = node.type.toString();
    costBreakdown[name] = (costBreakdown[name] ?? 0) + selfCost;

    const childrenCost = node.children.reduce(
      (sum, child) => sum + computeNodeCost(child, costConfig, costBreakdown),
      0
    );
    return selfCost + childrenCost;
  }
  return 1;
}

export function computeCellGridCost(
  cellGrid: Array<Array<{text: string}>>,
  parse: (text: string) => ast.ASTNode,
  costConfig: FunctionCostRegistry = DEFAULT_FUNCTION_COST_REGISTRY
): {totalCost: number; costBreakdown: {[key: string]: number}} {
  let totalCost = 0;
  const costBreakdown: {[key: string]: number} = {
    [CostBreakdownKey.CELL_COUNT]: cellGrid.length === 0 ? 0 : cellGrid.length * cellGrid[0].length,
  };
  for (const row of cellGrid) {
    for (const cell of row) {
      try {
        const astNode = parse(cell.text);
        totalCost += computeNodeCost(astNode, costConfig, costBreakdown);

        // parse errors are not a legitimate error in this case, since the user can legally
        // enter unparseable text, and we still want the cost analysis to continue if they do.
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch {
        totalCost += PARSE_ERROR_COST;
      }
    }
  }
  return {totalCost, costBreakdown};
}
